import React, {Component} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {v4 as uuidV4} from 'uuid';
import withConfig from 'config/withConfig';
import DateUtils from 'utils/date';
import TimeUtils from 'utils/time';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thur', 'Fri', 'Sat'];

export default EnhancedComponent => {
    class WithAirportDateRangePicker extends Component {
        static propTypes = {
            apiDateTimeFormat: PropTypes.string,
            displayDateFormat: PropTypes.string,
            id: PropTypes.string.isRequired,
            selectedStartDate: PropTypes.string,
            selectedEndDate: PropTypes.string,
            onSearchClick: PropTypes.func,
        };

        constructor(props) {
            super(props);

            const {
                id,
                selectedStartDate,
                selectedEndDate,
                displayDateFormat,
            } = props;

            if (!id) {
                throw new Error(
                    `The prop 'id' is required to make AirportDateRangePicker accessible for users using assistive technologies such as screen readers. Check the render method of AirportDateRangePicker.`
                );
            }

            this._TETHER_CLASS = `AirportDateRangePicker-tether-${id}`;
            this._disableAfterDate = moment()
                .add(12, 'months')
                .format(displayDateFormat);

            this.state = {
                showCalendar: false,
                selectedStartDate,
                selectedEndDate,
                adjustStartOrEnd: null,
                invalidStartDate: null,
                invalidEndDate: null,
                tetherKey: uuidV4(),
            };
        }

        _onCloseCalendar = evt => {
            this.setState({
                showCalendar: false,
                adjustStartOrEnd: null,
            });
        };

        _onStartInputFocus = evt => {
            this.setState({
                showCalendar: true,
                adjustStartOrEnd: 'start',
            });
        };

        _onEndInputFocus = evt => {
            this.setState({
                showCalendar: true,
                adjustStartOrEnd: 'end',
            });
        };

        _onInputChange = ({selectedStartDate, selectedEndDate}) => {
            this.setState({
                selectedStartDate,
                selectedEndDate,
            });
        };

        _onDateChange = ({
            selectedStartDate,
            selectedEndDate,
            nextAdjustedStartOrEnd,
            nextShowCalendar,
        }) => {
            this.setState((prevState, props) => {
                let adjustStartOrEnd = prevState.adjustStartOrEnd;

                if (selectedStartDate && selectedEndDate) {
                    adjustStartOrEnd = nextAdjustedStartOrEnd;
                } else if (!selectedEndDate) {
                    adjustStartOrEnd = 'end';
                }

                return {
                    showCalendar: nextShowCalendar,
                    adjustStartOrEnd,
                    selectedStartDate,
                    selectedEndDate,
                    invalidStartDate: prevState.invalidStartDate
                        ? !selectedStartDate
                        : null,
                    invalidEndDate: prevState.invalidEndDate
                        ? !selectedEndDate
                        : null,
                    tetherKey: uuidV4(),
                };
            });
        };

        _onSubmit = () => {
            const {onSearchClick, apiDateTimeFormat} = this.props;
            const {selectedStartDate, selectedEndDate} = this.state;

            if (selectedStartDate && selectedEndDate) {
                // In this component, we only allow the user to select start and end dates,
                // however, search and reservations APIs require both date and time.
                // Times are defaulted to 7AM on the start date to 11PM on the end date
                // as many operators during this time have reduced hours.
                onSearchClick({
                    starts: DateUtils.combineDateAndTime(
                        DateUtils.getAsMoment(selectedStartDate),
                        TimeUtils.getAsMoment('07:00 AM')
                    ).format(apiDateTimeFormat),
                    ends: DateUtils.combineDateAndTime(
                        DateUtils.getAsMoment(selectedEndDate),
                        TimeUtils.getAsMoment('11:00 PM')
                    ).format(apiDateTimeFormat),
                });
                this.setState({
                    showCalendar: false,
                    adjustStartOrEnd: null,
                });
            } else {
                this.setState((prevState, props) => {
                    return {
                        invalidStartDate: !prevState.selectedStartDate,
                        invalidEndDate: !prevState.selectedEndDate,
                    };
                });
            }
        };

        render() {
            const {
                showCalendar,
                selectedStartDate,
                selectedEndDate,
                adjustStartOrEnd,
                invalidStartDate,
                invalidEndDate,
                tetherKey,
            } = this.state;

            return (
                <EnhancedComponent
                    {...this.props}
                    weekdays={WEEKDAYS}
                    tetherKey={tetherKey}
                    showCalendar={showCalendar}
                    disableAfterDate={this._disableAfterDate}
                    selectedStartDate={selectedStartDate}
                    selectedEndDate={selectedEndDate}
                    adjustStartOrEnd={adjustStartOrEnd}
                    invalidStartDate={invalidStartDate}
                    invalidEndDate={invalidEndDate}
                    onStartInputFocus={this._onStartInputFocus}
                    onEndInputFocus={this._onEndInputFocus}
                    onDateChange={this._onDateChange}
                    onCloseCalendar={this._onCloseCalendar}
                    onSubmit={this._onSubmit}
                />
            );
        }
    }

    return withConfig(['apiDateTimeFormat', 'displayDateFormat'])(
        WithAirportDateRangePicker
    );
};
